"use strict";

/**
 * Isolated client and pure helpers for the dynamic ACEP worksheet service.
 *
 * Change set 1 of ACEP_WORKSHEET_SERVICE_IMPLEMENTATION_PLAN.md. Nothing in
 * this module is wired into the store or the editors yet; it exists so the
 * protocol, date, validation, adaptation, and provenance behavior can be
 * exercised on its own by scripts/check-worksheet-helpers.js.
 *
 * The module is CommonJS on purpose. package.json has no "type": "module",
 * so plain Node can require this file directly, while webpack 4 still
 * consumes it through the editors' ESM `import` statements.
 *
 * aws-amplify is required lazily inside the default token provider so that
 * loading this module in plain Node (no node_modules on the host) does not
 * pull in the browser dependency graph.
 */

const ACTIVE_WORKSHEET_BASE_URL =
  "https://5h61kqlfp7.execute-api.us-east-1.amazonaws.com";

const TECHNICAL_EMERGENCY_MEDICINE = "technical_emergency_medicine";

// Service `code` for the expected "this facility/DOS has no worksheet" answer.
const NO_ACTIVE_ASSIGNMENT_CODE = "no_active_assignment";

// Facility-settings tool flag that makes a facility eligible for the ACEP
// intervention list. Only an exact match counts.
const ACEP_TOOL_FLAG = "EM_TOOL_ACEP_LT";

/**
 * Request statuses shared by this client and the Vuex request state added in
 * change set 2. `idle`, `not_applicable`, `loading`, and `superseded` are never
 * returned by fetchActiveWorksheet(); they are owned by the store lifecycle and
 * live here so the two change sets cannot drift apart.
 *
 * `superseded` is the only one of the four that is never a *state* value
 * either. Change set 2's action returns it when a newer request has taken over
 * before this one completed, so a caller can reject a stale completion with a
 * single status check.
 */
const WORKSHEET_REQUEST_STATUS = {
  IDLE: "idle",
  NOT_APPLICABLE: "not_applicable",
  INVALID_CONTEXT: "invalid_context",
  LOADING: "loading",
  SUPERSEDED: "superseded",
  READY: "ready",
  UNAVAILABLE: "unavailable",
  AUTH_ERROR: "auth_error",
  FORBIDDEN: "forbidden",
  REQUEST_ERROR: "request_error",
  NETWORK_ERROR: "network_error",
  SERVICE_ERROR: "service_error",
  INVALID_RESPONSE: "invalid_response"
};

/**
 * Wall-clock bound on one active-worksheet request, from dispatch to settled
 * result, covering both the fetch and the response-body read.
 *
 * The store owns the `AbortController`; the constant lives here for the same
 * reason `WORKSHEET_REQUEST_STATUS` does — so the client half and the store
 * half cannot drift. 20s is long enough for a cold Lambda plus a slow
 * hospital network and short enough that a coder is not left with a disabled
 * picker and no explanation.
 */
const WORKSHEET_REQUEST_TIMEOUT_MS = 20000;

/* ------------------------------------------------------------------ *
 * Facility eligibility
 * ------------------------------------------------------------------ */

/**
 * Is this facility assigned the ACEP intervention list?
 *
 * The legacy store calls `facTools.indexOf()` directly and throws when
 * `facSettings.tools` is missing. This helper never throws, so an unloaded or
 * malformed settings object simply means "not eligible" and no request.
 *
 * @param {*} facTools value of the store's `facTools` getter
 * @returns {boolean}
 */
function isAcepToolEligible(facTools) {
  if (!Array.isArray(facTools)) {
    return false;
  }
  return facTools.indexOf(ACEP_TOOL_FLAG) !== -1;
}

/* ------------------------------------------------------------------ *
 * Date of service
 * ------------------------------------------------------------------ */

// Accepts the stored ISO form and the non-zero-padded form the editors'
// editDateFix() produces (2026-7-9).
const ISO_DOS_PATTERN = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;

// Accepts the legacy/manual US form (7/9/2026).
const SLASH_DOS_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

// Accepts the encounter model's ISO 8601 datetime form (confirmed 2026-07-29:
// the encounter GET body stores initial_dos as an ISO 8601 string). Only the
// leading date components are used, taken from the string itself — never
// through a Date object — so the stored calendar date survives verbatim in
// every client timezone. The time part must still be a real time, so the
// clock and offset components are captured here and range-checked below
// rather than merely shape-matched.
//
// Groups: 1 year, 2 month, 3 day, 4 hour, 5 minute, 6 second (optional),
// 7 offset hours, 8 offset minutes (both absent for `Z` and for no offset).
const ISO_DATETIME_DOS_PATTERN =
  /^(\d{4})-(\d{1,2})-(\d{1,2})[Tt ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(?:[Zz]|[+-](\d{2}):?(\d{2}))?$/;

const MONTH_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// Supported date-of-service year range.
//
// A DOS outside it is a data-entry or parsing artifact rather than an
// encounter — this application codes present-day emergency visits, so nothing
// before 1900 is a real service date, and a year below 1000 additionally
// cannot round-trip through the four-digit `YYYY-MM-DD` output (`0000-01-01`
// used to normalize to `0-01-01`, an invalid query value). 2999 is the top of
// the four-digit space, which keeps the emitted width constant for every
// value the range admits.
const MIN_DOS_YEAR = 1900;
const MAX_DOS_YEAR = 2999;

function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInMonth(year, month) {
  if (month === 2 && isLeapYear(year)) {
    return 29;
  }
  return MONTH_LENGTHS[month - 1];
}

function isRealCalendarDate(year, month, day) {
  if (month < 1 || month > 12) {
    return false;
  }
  if (day < 1) {
    return false;
  }
  return day <= daysInMonth(year, month);
}

function isSupportedDosYear(year) {
  return year >= MIN_DOS_YEAR && year <= MAX_DOS_YEAR;
}

/**
 * Range-check the captured clock and offset components of a datetime match.
 *
 * The shape check alone accepted `2026-07-29T99:99:99Z` and
 * `2026-07-29T24:00:00Z`. A value whose time half is impossible is a
 * malformed string, not a date of service to guess the calendar day from, so
 * it is rejected rather than truncated. Every component here is a digit run
 * from the pattern, so there is nothing to check but the upper bounds.
 *
 * Second 60 (a leap second) is rejected: no producer of this field emits one,
 * and accepting it would mean accepting the whole 60-61 range on faith.
 */
function isRealClockTime(match) {
  if (Number(match[4]) > 23 || Number(match[5]) > 59) {
    return false;
  }
  if (match[6] !== undefined && Number(match[6]) > 59) {
    return false;
  }
  // A numeric UTC offset. 14:00 is the largest offset in use, and the minute
  // component is a normal minute count.
  if (match[7] !== undefined) {
    if (Number(match[7]) > 14 || Number(match[8]) > 59) {
      return false;
    }
  }
  return true;
}

function padTwo(value) {
  return value < 10 ? "0" + value : String(value);
}

// The supported range keeps every year four digits already; this is the
// second lock, so widening MIN_DOS_YEAR can never reintroduce an unpadded
// year in the emitted value.
function padFour(value) {
  let text = String(value);
  while (text.length < 4) {
    text = "0" + text;
  }
  return text;
}

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim() !== "";
}

function isPresent(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

/**
 * Normalize a date of service to zero-padded `YYYY-MM-DD`.
 *
 * Works on the string's date components only. It deliberately never round
 * trips through `new Date(value).toISOString()`, which shifts the calendar
 * date for any client whose timezone offset is not UTC.
 *
 * Returns null for empty, non-string, impossible, out-of-range, and
 * unrecognized values so callers fail closed instead of sending an invalid
 * query.
 *
 * @param {*} value
 * @returns {string|null}
 */
function normalizeDos(value) {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }

  let year;
  let month;
  let day;

  const isoMatch = ISO_DOS_PATTERN.exec(trimmed);
  if (isoMatch) {
    year = Number(isoMatch[1]);
    month = Number(isoMatch[2]);
    day = Number(isoMatch[3]);
  } else {
    const datetimeMatch = ISO_DATETIME_DOS_PATTERN.exec(trimmed);
    if (datetimeMatch) {
      if (!isRealClockTime(datetimeMatch)) {
        return null;
      }
      year = Number(datetimeMatch[1]);
      month = Number(datetimeMatch[2]);
      day = Number(datetimeMatch[3]);
    } else {
      const slashMatch = SLASH_DOS_PATTERN.exec(trimmed);
      if (!slashMatch) {
        return null;
      }
      month = Number(slashMatch[1]);
      day = Number(slashMatch[2]);
      year = Number(slashMatch[3]);
    }
  }

  if (!isSupportedDosYear(year) || !isRealCalendarDate(year, month, day)) {
    return null;
  }

  return padFour(year) + "-" + padTwo(month) + "-" + padTwo(day);
}

/**
 * Derive the normalized lookup date for an encounter.
 *
 * `initial_dos` wins whenever it is present. A present but invalid
 * `initial_dos` is not silently replaced with `dos`; it resolves to null so
 * the caller reports an invalid context rather than resolving a worksheet for
 * a different day.
 *
 * @param {object} encounter
 * @returns {string|null}
 */
function resolveActiveDos(encounter) {
  if (!encounter || typeof encounter !== "object") {
    return null;
  }

  const candidate = isPresent(encounter.initial_dos)
    ? encounter.initial_dos
    : encounter.dos;

  return normalizeDos(candidate);
}

/* ------------------------------------------------------------------ *
 * URL construction
 * ------------------------------------------------------------------ */

/**
 * Build the active-worksheet URL with encoded path and query values.
 *
 * @param {{client: string, facility: string, initialDos: string,
 *          worksheetType?: string, baseUrl?: string}} params
 * @returns {string}
 */
function buildActiveWorksheetUrl(params) {
  const options = params || {};
  const worksheetType = options.worksheetType || TECHNICAL_EMERGENCY_MEDICINE;
  const baseUrl = String(options.baseUrl || ACTIVE_WORKSHEET_BASE_URL).replace(
    /\/+$/,
    ""
  );

  if (!isNonEmptyString(options.client)) {
    throw new TypeError("activeWorksheet: client is required");
  }
  if (!isNonEmptyString(options.facility)) {
    throw new TypeError("activeWorksheet: facility is required");
  }
  if (!isNonEmptyString(options.initialDos)) {
    throw new TypeError("activeWorksheet: initialDos is required");
  }

  return (
    baseUrl +
    "/api/v1/clients/" +
    encodeURIComponent(options.client) +
    "/facilities/" +
    encodeURIComponent(options.facility) +
    "/worksheet-types/" +
    encodeURIComponent(worksheetType) +
    "/active-worksheet?initial_dos=" +
    encodeURIComponent(options.initialDos)
  );
}

/* ------------------------------------------------------------------ *
 * Response validation and adaptation
 * ------------------------------------------------------------------ */

// Number.isInteger is ES2015; core-js polyfills the app build, but this
// module also runs in plain Node, so keep the check self-contained.
function isInteger(value) {
  return (
    typeof value === "number" && isFinite(value) && Math.floor(value) === value
  );
}

/**
 * Read an entry's E/M level as a number.
 *
 * The spec types `level` as a string, and the editors compute the persisted
 * E/M code with `Math.max()` over the selected levels, so a level that is not
 * a number is how `"NaN"` got saved as a code. This is the single reading of
 * the field: the validator below rejects anything it cannot turn into a real
 * level, which is what keeps that arithmetic honest.
 *
 * @param {*} entry
 * @returns {number} NaN when the level is unusable
 */
function worksheetEntryLevel(entry) {
  const raw = entry ? entry.level : undefined;

  if (typeof raw === "number") {
    return isFinite(raw) ? raw : NaN;
  }
  if (typeof raw === "string" && raw.trim() !== "") {
    return Number(raw);
  }
  return NaN;
}

/**
 * Validate one worksheet entry (P1.3).
 *
 * Defense in depth: the service schema declares every entry field required,
 * so this should never fire against the real service. It exists because the
 * consequences of a malformed entry reaching the picker are silent and
 * persistent — a `"NaN"` E/M code, an intervention with no reportable id, or
 * a blank picker row.
 *
 * Only `active === true` entries are checked. An inactive entry is filtered
 * out by adaptWorksheetEntries() before any consumer sees it, so its
 * completeness is not this validator's business and a retired row with stale
 * fields must not fail an otherwise good response.
 *
 * `order` is read through orderValue() — declared below, hoisted — so this
 * validator and adaptWorksheetEntries() cannot disagree about what a usable
 * order is.
 *
 * Reasons are diagnostics for the request state and logs. They are never
 * display text.
 *
 * @param {*} entry
 * @param {number} index position in the response `entries` array
 * @returns {{valid: boolean, reason?: string}}
 */
function validateWorksheetEntry(entry, index) {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
    return { valid: false, reason: "entry_" + index + "_not_object" };
  }

  if (entry.active !== true) {
    return { valid: true };
  }

  if (!isNonEmptyString(entry.id)) {
    return { valid: false, reason: "entry_" + index + "_id_invalid" };
  }
  if (!isNonEmptyString(entry.description)) {
    return { valid: false, reason: "entry_" + index + "_description_invalid" };
  }

  const level = worksheetEntryLevel(entry);
  if (!isInteger(level) || level <= 0) {
    return { valid: false, reason: "entry_" + index + "_level_invalid" };
  }

  if (!isInteger(orderValue(entry))) {
    return { valid: false, reason: "entry_" + index + "_order_invalid" };
  }

  return { valid: true };
}

/**
 * Validate a 200 response envelope before any of it can become a catalog.
 *
 * @param {*} body parsed response body
 * @param {{client: string, facility: string, worksheetType?: string}} expected
 * @returns {{valid: boolean, reason?: string, worksheet?: object,
 *            entries?: Array}}
 */
function validateActiveWorksheetResponse(body, expected) {
  const context = expected || {};
  const worksheetType = context.worksheetType || TECHNICAL_EMERGENCY_MEDICINE;

  // Without a real expected client and facility the echo comparison below
  // would pass on undefined === undefined, so refuse the comparison instead.
  if (!isNonEmptyString(context.client) || !isNonEmptyString(context.facility)) {
    return { valid: false, reason: "expected_context_invalid" };
  }
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    return { valid: false, reason: "response_not_object" };
  }
  if (body.client !== context.client) {
    return { valid: false, reason: "client_mismatch" };
  }
  if (body.facility !== context.facility) {
    return { valid: false, reason: "facility_mismatch" };
  }
  if (body.worksheet_type !== worksheetType) {
    return { valid: false, reason: "worksheet_type_mismatch" };
  }
  if (!isNonEmptyString(body.worksheet_id)) {
    return { valid: false, reason: "worksheet_id_invalid" };
  }
  if (!isInteger(body.revision)) {
    return { valid: false, reason: "revision_invalid" };
  }
  if (!isNonEmptyString(body.effective_date)) {
    return { valid: false, reason: "effective_date_invalid" };
  }
  if (!Array.isArray(body.entries)) {
    return { valid: false, reason: "entries_not_array" };
  }

  // One bad entry fails the whole response. A partial catalog would be worse
  // than none: the coder cannot tell that a row is missing, so a silently
  // dropped intervention would look like a worksheet that does not have it.
  for (let i = 0; i < body.entries.length; i++) {
    const entryCheck = validateWorksheetEntry(body.entries[i], i);
    if (!entryCheck.valid) {
      return { valid: false, reason: entryCheck.reason };
    }
  }

  return {
    valid: true,
    worksheet: {
      worksheet_id: body.worksheet_id,
      revision: body.revision,
      effective_date: body.effective_date,
      worksheet_type: body.worksheet_type
    },
    entries: body.entries
  };
}

/**
 * Read an entry's sort key.
 *
 * The spec types `order` as an integer. Anything that is not a number or a
 * non-blank numeric string is unusable rather than zero, so a null or blank
 * `order` cannot jump ahead of a real one.
 */
function orderValue(entry) {
  const raw = entry ? entry.order : undefined;

  if (typeof raw === "number") {
    return raw;
  }
  if (typeof raw === "string" && raw.trim() !== "") {
    return Number(raw);
  }
  return NaN;
}

function compareByOrder(a, b) {
  const left = orderValue(a);
  const right = orderValue(b);
  const leftInvalid = isNaN(left);
  const rightInvalid = isNaN(right);

  if (leftInvalid && rightInvalid) {
    return 0;
  }
  if (leftInvalid) {
    return 1;
  }
  if (rightInvalid) {
    return -1;
  }
  return left - right;
}

/**
 * Keep only selectable entries, ascending by `order`.
 *
 * Returns a new array and never mutates the response payload. Entry objects
 * are passed through whole because the picker uses `return-object` and the
 * encounter persists the complete selected objects.
 *
 * @param {Array} entries
 * @returns {Array}
 */
function adaptWorksheetEntries(entries) {
  if (!Array.isArray(entries)) {
    return [];
  }

  return entries
    .filter(function (entry) {
      return entry && typeof entry === "object" && entry.active === true;
    })
    .sort(compareByOrder);
}

/* ------------------------------------------------------------------ *
 * Worksheet provenance
 * ------------------------------------------------------------------ */

/**
 * Build one `activeEnc.worksheet_versions` record.
 *
 * @param {string} dos normalized DOS actually sent to the service
 * @param {{worksheet_id: string, revision: number, effective_date: string,
 *          worksheet_type?: string}} worksheet
 * @returns {{dos: string, worksheet_type: string, worksheet_id: string,
 *            revision: number, effective_date: string}}
 */
function buildWorksheetVersionRecord(dos, worksheet) {
  if (!isNonEmptyString(dos)) {
    throw new TypeError("activeWorksheet: dos is required");
  }
  if (!worksheet || typeof worksheet !== "object") {
    throw new TypeError("activeWorksheet: worksheet is required");
  }

  return {
    dos: dos,
    worksheet_type: worksheet.worksheet_type || TECHNICAL_EMERGENCY_MEDICINE,
    worksheet_id: worksheet.worksheet_id,
    revision: worksheet.revision,
    effective_date: worksheet.effective_date
  };
}

function matchesWorksheetVersionKey(candidate, record) {
  return Boolean(
    candidate &&
      typeof candidate === "object" &&
      candidate.dos === record.dos &&
      candidate.worksheet_type === record.worksheet_type
  );
}

/**
 * Upsert a provenance record by the composite key (dos, worksheet_type).
 *
 * Pure: returns a new array and leaves the supplied collection and its other
 * records untouched. Replacing an existing record merges over it, so unknown
 * future fields written by another client survive.
 *
 * Duplicates of the key are repaired, not preserved (P3.1): the first match
 * keeps its position and is merged into, and every later match is dropped.
 * This frontend does not produce duplicates from clean input, but malformed or
 * externally produced encounter data would otherwise stay outside the stated
 * uniqueness invariant, and reporting joins on this key — a leftover duplicate
 * would attribute the same DOS to two worksheet revisions.
 *
 * @param {Array|undefined|null} worksheetVersions
 * @param {object} record
 * @returns {Array}
 */
function upsertWorksheetVersion(worksheetVersions, record) {
  if (!record || typeof record !== "object") {
    throw new TypeError("activeWorksheet: record is required");
  }
  if (!isNonEmptyString(record.dos)) {
    throw new TypeError("activeWorksheet: record.dos is required");
  }
  if (!isNonEmptyString(record.worksheet_type)) {
    throw new TypeError("activeWorksheet: record.worksheet_type is required");
  }

  const existing = Array.isArray(worksheetVersions) ? worksheetVersions : [];
  const next = [];
  let merged = false;

  for (let i = 0; i < existing.length; i++) {
    const candidate = existing[i];

    if (matchesWorksheetVersionKey(candidate, record)) {
      if (!merged) {
        merged = true;
        next.push(Object.assign({}, candidate, record));
      }
      continue;
    }

    next.push(candidate);
  }

  if (!merged) {
    next.push(Object.assign({}, record));
  }

  return next;
}

/* ------------------------------------------------------------------ *
 * Service client
 * ------------------------------------------------------------------ */

function buildResult(status, extra) {
  return Object.assign(
    {
      status: status,
      httpStatus: null,
      worksheet: null,
      entries: [],
      serviceRequestId: null,
      serviceCode: null,
      reason: null
    },
    extra || {}
  );
}

function describeError(error) {
  if (error && typeof error.message === "string" && error.message) {
    return error.message;
  }
  return "request_failed";
}

function readHeader(response, name) {
  const headers = response ? response.headers : null;
  if (!headers || typeof headers.get !== "function") {
    return null;
  }
  const value = headers.get(name);
  return isNonEmptyString(value) ? value : null;
}

function defaultFetch() {
  if (
    typeof globalThis !== "undefined" &&
    typeof globalThis.fetch === "function"
  ) {
    return globalThis.fetch.bind(globalThis);
  }
  return null;
}

/**
 * Build an access-token provider over an Amplify-shaped `Auth`.
 *
 * The worksheet service authenticates with the Cognito *access* token, unlike
 * the legacy store helper that sends a raw ID token. The legacy helper is left
 * alone; there is no ID-token retry here. A fresh session is requested per
 * call so a rotated token is never served from a stale closure, and the token
 * is never cached or logged.
 *
 * @param {{currentSession: Function}} auth
 * @returns {function(): Promise<string>}
 */
function createAccessTokenProvider(auth) {
  if (!auth || typeof auth.currentSession !== "function") {
    throw new TypeError("activeWorksheet: auth.currentSession is required");
  }

  return function () {
    return Promise.resolve()
      .then(function () {
        return auth.currentSession();
      })
      .then(function (session) {
        return session.getAccessToken().getJwtToken();
      });
  };
}

function defaultGetAccessToken() {
  // Lazy require: keeps plain-Node loads free of the browser dependency graph.
  return createAccessTokenProvider(require("aws-amplify").Auth)();
}

/**
 * Request the active worksheet for a client, facility, and normalized DOS.
 *
 * Never throws and never rejects for an expected failure: every outcome is a
 * classified result object. The access token is neither logged nor returned.
 *
 * `initialDos` must already be a normalized `YYYY-MM-DD` value, so the DOS in
 * the request cannot diverge from the DOS later persisted in
 * `worksheet_versions`.
 *
 * @param {{client: string, facility: string, initialDos: string,
 *          worksheetType?: string, baseUrl?: string}} params
 * @param {{fetchImpl?: Function, getAccessToken?: Function,
 *          signal?: object}} [options]
 * @returns {Promise<{status: string, httpStatus: number|null,
 *          worksheet: object|null, entries: Array,
 *          serviceRequestId: string|null, serviceCode: string|null,
 *          reason: string|null}>}
 */
function fetchActiveWorksheet(params, options) {
  const request = params || {};
  const deps = options || {};
  const worksheetType = request.worksheetType || TECHNICAL_EMERGENCY_MEDICINE;
  const fetchImpl = deps.fetchImpl || defaultFetch();
  const getAccessToken = deps.getAccessToken || defaultGetAccessToken;

  if (!isNonEmptyString(request.client)) {
    return Promise.resolve(
      buildResult(WORKSHEET_REQUEST_STATUS.INVALID_CONTEXT, {
        reason: "missing_client"
      })
    );
  }
  if (!isNonEmptyString(request.facility)) {
    return Promise.resolve(
      buildResult(WORKSHEET_REQUEST_STATUS.INVALID_CONTEXT, {
        reason: "missing_facility"
      })
    );
  }
  if (
    !isNonEmptyString(request.initialDos) ||
    normalizeDos(request.initialDos) !== request.initialDos
  ) {
    return Promise.resolve(
      buildResult(WORKSHEET_REQUEST_STATUS.INVALID_CONTEXT, {
        reason: "invalid_dos"
      })
    );
  }
  if (typeof fetchImpl !== "function") {
    return Promise.resolve(
      buildResult(WORKSHEET_REQUEST_STATUS.NETWORK_ERROR, {
        reason: "fetch_unavailable"
      })
    );
  }

  let url;
  try {
    url = buildActiveWorksheetUrl({
      client: request.client,
      facility: request.facility,
      initialDos: request.initialDos,
      worksheetType: worksheetType,
      baseUrl: request.baseUrl
    });
  } catch (error) {
    return Promise.resolve(
      buildResult(WORKSHEET_REQUEST_STATUS.INVALID_CONTEXT, {
        reason: describeError(error)
      })
    );
  }

  return requestActiveWorksheet(url, {
    client: request.client,
    facility: request.facility,
    worksheetType: worksheetType,
    fetchImpl: fetchImpl,
    getAccessToken: getAccessToken,
    signal: deps.signal
  });
}

function requestActiveWorksheet(url, context) {
  return Promise.resolve()
    .then(function () {
      return context.getAccessToken();
    })
    .then(
      function (token) {
        if (!isNonEmptyString(token)) {
          return buildResult(WORKSHEET_REQUEST_STATUS.AUTH_ERROR, {
            reason: "missing_access_token"
          });
        }
        return sendRequest(url, token, context);
      },
      function (error) {
        return buildResult(WORKSHEET_REQUEST_STATUS.AUTH_ERROR, {
          reason: describeError(error)
        });
      }
    );
}

function sendRequest(url, token, context) {
  const init = {
    method: "GET",
    cache: "no-cache",
    headers: {
      Accept: "application/json",
      Authorization: "Bearer " + token
    },
    referrerPolicy: "no-referrer"
  };

  if (context.signal) {
    init.signal = context.signal;
  }

  return Promise.resolve()
    .then(function () {
      return context.fetchImpl(url, init);
    })
    .then(
      function (response) {
        return readResponse(response, context);
      },
      function (error) {
        return buildResult(WORKSHEET_REQUEST_STATUS.NETWORK_ERROR, {
          reason: describeError(error)
        });
      }
    );
}

function readResponse(response, context) {
  const httpStatus =
    response && typeof response.status === "number" ? response.status : null;
  const headerRequestId = readHeader(response, "x-request-id");

  return Promise.resolve()
    .then(function () {
      return response && typeof response.text === "function"
        ? response.text()
        : "";
    })
    .then(
      function (raw) {
        return classifyResponse(raw, httpStatus, headerRequestId, context);
      },
      function (error) {
        return buildResult(WORKSHEET_REQUEST_STATUS.NETWORK_ERROR, {
          httpStatus: httpStatus,
          serviceRequestId: headerRequestId,
          reason: describeError(error)
        });
      }
    );
}

function classifyResponse(raw, httpStatus, headerRequestId, context) {
  let body = null;
  let parseFailed = false;

  if (isNonEmptyString(raw)) {
    try {
      body = JSON.parse(raw);
    } catch (error) {
      parseFailed = true;
    }
  }

  const isBodyObject = Boolean(body) && typeof body === "object";
  const serviceRequestId =
    headerRequestId ||
    (isBodyObject && isNonEmptyString(body.request_id) ? body.request_id : null);
  const serviceCode =
    isBodyObject && isNonEmptyString(body.code) ? body.code : null;
  const serviceDetail =
    isBodyObject && isNonEmptyString(body.detail) ? body.detail : null;

  const base = {
    httpStatus: httpStatus,
    serviceRequestId: serviceRequestId,
    serviceCode: serviceCode
  };

  const isSuccess =
    httpStatus !== null && httpStatus >= 200 && httpStatus < 300;

  if (isSuccess) {
    if (parseFailed) {
      return buildResult(
        WORKSHEET_REQUEST_STATUS.INVALID_RESPONSE,
        Object.assign({}, base, { reason: "invalid_json" })
      );
    }
    if (body === null) {
      return buildResult(
        WORKSHEET_REQUEST_STATUS.INVALID_RESPONSE,
        Object.assign({}, base, { reason: "empty_body" })
      );
    }

    const validation = validateActiveWorksheetResponse(body, {
      client: context.client,
      facility: context.facility,
      worksheetType: context.worksheetType
    });

    if (!validation.valid) {
      return buildResult(
        WORKSHEET_REQUEST_STATUS.INVALID_RESPONSE,
        Object.assign({}, base, { reason: validation.reason })
      );
    }

    return buildResult(
      WORKSHEET_REQUEST_STATUS.READY,
      Object.assign({}, base, {
        worksheet: validation.worksheet,
        entries: adaptWorksheetEntries(validation.entries)
      })
    );
  }

  if (httpStatus === 404 && serviceCode === NO_ACTIVE_ASSIGNMENT_CODE) {
    return buildResult(
      WORKSHEET_REQUEST_STATUS.UNAVAILABLE,
      Object.assign({}, base, {
        reason: serviceDetail || NO_ACTIVE_ASSIGNMENT_CODE
      })
    );
  }

  return buildResult(
    classifyFailureStatus(httpStatus),
    Object.assign({}, base, {
      reason: serviceDetail || failureReason(httpStatus, parseFailed)
    })
  );
}

function classifyFailureStatus(httpStatus) {
  if (httpStatus === 401) {
    return WORKSHEET_REQUEST_STATUS.AUTH_ERROR;
  }
  if (httpStatus === 403) {
    return WORKSHEET_REQUEST_STATUS.FORBIDDEN;
  }
  if (httpStatus === 404) {
    // A 404 without the documented service code is not the expected
    // "no assignment" answer; it usually means the route itself is wrong.
    return WORKSHEET_REQUEST_STATUS.SERVICE_ERROR;
  }
  if (httpStatus !== null && httpStatus >= 400 && httpStatus < 500) {
    return WORKSHEET_REQUEST_STATUS.REQUEST_ERROR;
  }
  return WORKSHEET_REQUEST_STATUS.SERVICE_ERROR;
}

function failureReason(httpStatus, parseFailed) {
  if (parseFailed) {
    return "invalid_json";
  }
  if (httpStatus === null) {
    return "unknown_response";
  }
  return "http_" + httpStatus;
}

module.exports = {
  ACTIVE_WORKSHEET_BASE_URL: ACTIVE_WORKSHEET_BASE_URL,
  TECHNICAL_EMERGENCY_MEDICINE: TECHNICAL_EMERGENCY_MEDICINE,
  NO_ACTIVE_ASSIGNMENT_CODE: NO_ACTIVE_ASSIGNMENT_CODE,
  ACEP_TOOL_FLAG: ACEP_TOOL_FLAG,
  WORKSHEET_REQUEST_STATUS: WORKSHEET_REQUEST_STATUS,
  WORKSHEET_REQUEST_TIMEOUT_MS: WORKSHEET_REQUEST_TIMEOUT_MS,
  isAcepToolEligible: isAcepToolEligible,
  createAccessTokenProvider: createAccessTokenProvider,
  normalizeDos: normalizeDos,
  resolveActiveDos: resolveActiveDos,
  buildActiveWorksheetUrl: buildActiveWorksheetUrl,
  worksheetEntryLevel: worksheetEntryLevel,
  validateWorksheetEntry: validateWorksheetEntry,
  validateActiveWorksheetResponse: validateActiveWorksheetResponse,
  adaptWorksheetEntries: adaptWorksheetEntries,
  buildWorksheetVersionRecord: buildWorksheetVersionRecord,
  upsertWorksheetVersion: upsertWorksheetVersion,
  fetchActiveWorksheet: fetchActiveWorksheet
};
